import { useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";
import { BuzerIcon } from "../../BuzerIcons";
import { Avatar, Chk, CompletionBurst, EffectBurst, Glyph, IconPicker, InstallGuide, Modal, useProfileModal } from "../../components/ui";
import { HEAD_FLAIRS, HeadFlair } from "../../components/ui/HeadFlair";
import { FramePreview } from "../../components/ui/Avatar";
import { Button, Caption, IconButton, List, ListRow, Lead, SectionHeader, Segmented, Tag } from "../../components/ui/ds";
import { Icon } from "../../icons";
import { api } from "../../lib/api";
import { fmtDuePretty } from "../../lib/date-sk";
import { shrinkImage } from "../../lib/image";
import { APP_THEME_SWATCHES, EFFECTS, SWATCH, SHOP_ITEM_ICON } from "../../lib/constants";
import { getLevel, nextLevel } from "../../lib/levels";
import { verLabel } from "../../lib/format";
import { enablePush, isStandalone, testPush } from "../../lib/pwa";
import { session, setSession } from "../../lib/session";
import { REDUCE_MOTION, applyAppTheme, applyFont, getAppFont, getAppTheme, previewAppTheme, previewFont } from "../../lib/theme";
import { st } from "../../styles.js";
import "./tryon.css";

export function ProfilRow({ icon, label, hint, onClick, disabled }) {
  return (
    <button onClick={onClick} disabled={disabled} className={disabled ? "" : "sheet-row"}
      style={{ ...st.profRow, ...(disabled ? { opacity: 0.55, cursor: "default" } : {}) }}>
      <span className="bz-lead bz-lead--circle">{icon}</span>
      <span style={{ flex: 1, textAlign: "left" }}><span style={st.sheetLabel}>{label}</span></span>
      {hint ? <span style={st.profRowHint}>{hint}</span> : <span style={st.sheetChevron}>›</span>}
    </button>
  );
}
// Retencia AI rozhovorov — najlacnejšia ochrana je dáta vôbec nemať. 0 = navždy.
// Hodnoty musia sedieť s db.CHAT_RETENTION_CHOICES.
export const CHAT_RETENTION = [
  { value: 30, label: "30 dní" },
  { value: 90, label: "90 dní" },
  { value: 180, label: "180 dní" },
  { value: 0, label: "Navždy" },
];
export function chatRetentionLabel(v) {
  return (CHAT_RETENTION.find((o) => o.value === v) || CHAT_RETENTION[1]).label;
}
export function ChatRetentionModal({ value, onClose, reload }) {
  const [busy, setBusy] = useState(false);
  const [note, setNote] = useState("");
  async function pick(days) {
    if (days === value) return;
    setBusy(true);
    try {
      const r = await api("/api/account/chat-retention", { method: "POST", body: { days } });
      const n = (r.deleted && r.deleted.ai_chat_messages) || 0;
      setNote(n ? `Zmazaných ${n} starších správ.` : "Nastavené — mazať zatiaľ nebolo čo.");
      reload && reload();
    } catch { setNote("Nepodarilo sa uložiť."); }
    setBusy(false);
  }
  return (
    <Modal center title="Ako dlho držať rozhovory" icon="ai" maxWidth={420} onClose={onClose}>
      <div style={st.taskDesc}>
        Rozhovory s asistentom sú to najosobnejšie, čo v appke máš. Po zvolenom čase ich
        automaticky zmažeme. Prišpendlené rozhovory ostávajú — pripnutie berieme ako „toto si nechaj“.
      </div>
      <Segmented style={{ marginTop: 14 }} options={CHAT_RETENTION} value={value}
        onChange={pick} disabled={busy} />
      {note && <div style={{ ...st.taskDesc, marginTop: 12 }}>{note}</div>}
    </Modal>
  );
}
// v8 Pilier 1 — GDPR transparency: view / delete / wipe / toggle the AI's soft memory.
export function AiMemoryModal({ onClose }) {
  const [d, setD] = useState(null);
  const [busy, setBusy] = useState(false);
  const [newFact, setNewFact] = useState("");
  const [editSummary, setEditSummary] = useState(false);
  const [draft, setDraft] = useState("");
  const load = useCallback(async () => { try { setD(await api("/api/ai/memory")); } catch { setD({ enabled: false, facts: [], summary: "", open_loops: [] }); } }, []);
  useEffect(() => { load(); }, [load]);
  async function toggle() { setBusy(true); try { await api("/api/ai/memory/toggle", { method: "POST", body: { enabled: !d.enabled } }); } catch (e) { /* */ } await load(); setBusy(false); }
  async function delFact(id) { await api("/api/ai/memory/fact/" + id, { method: "DELETE" }); load(); }
  async function addFact() { const t = newFact.trim(); if (!t) return; setBusy(true); try { await api("/api/ai/memory/fact", { method: "POST", body: { text: t } }); setNewFact(""); } catch (e) { /* */ } await load(); setBusy(false); }
  async function wipe() { if (!confirm("Vymazať celú pamäť? Kouč zabudne všetko, čo o tebe vie.")) return; await api("/api/ai/memory/wipe", { method: "POST" }); load(); }
  // v30 — súhrn sa dá prepísať vlastnými slovami; prázdny text ho vráti pod správu AI
  async function saveSummary(text) {
    setBusy(true);
    try { await api("/api/ai/memory/summary", { method: "POST", body: { text } }); } catch (e) { /* */ }
    setEditSummary(false); await load(); setBusy(false);
  }
  return (
    <Modal center title="Čo si o tebe pamätám" icon="ai" maxWidth={480} onClose={onClose}>
      <div style={st.taskDesc}>AI kouč si drží jemnú pamäť (tvoje motivácie, vzorce, čo ti tónovo sadne), aby ti vedel lepšie pomôcť. Tvrdé dáta (body, série) si neukladá — tie vždy číta naživo. Máš to plne pod kontrolou.</div>
      {!d ? <div style={{ ...st.empty, marginTop: 16 }}>…</div> : (<>
        <div style={{ ...st.profRow, cursor: "default", marginTop: 12 }}>
          <span className="bz-lead bz-lead--circle"><BuzerIcon name="ai" size={20} /></span>
          <span style={{ flex: 1, textAlign: "left", minWidth: 0 }}>
            <span style={st.sheetLabel}>Pamäť zapnutá</span>
            <span style={st.sheetDesc}>{d.enabled ? "Kouč si pamätá kontext o tebe." : "Vypnutá — nič si neukladá."}</span>
          </span>
          <button type="button" role="switch" aria-checked={!!d.enabled} onClick={toggle}
            style={{ ...st.switch, ...(d.enabled ? st.switchOn : {}), flex: "none" }}>
            <span style={{ ...st.switchKnob, ...(d.enabled ? st.switchKnobOn : {}) }} />
          </button>
        </div>
        {d.enabled && (<>
          {editSummary ? (
            <div style={{ marginTop: 12 }}>
              <textarea className="field" style={{ ...st.field, width: "100%", minHeight: 120, resize: "vertical" }}
                value={draft} maxLength={d.summary_max || 600} autoFocus
                placeholder="Napíš vlastnými slovami, čo má o tebe kouč vedieť."
                onChange={(e) => setDraft(e.target.value)} />
              <div style={{ display: "flex", gap: 8, marginTop: 8, alignItems: "center", flexWrap: "wrap" }}>
                <Button variant="primary" disabled={busy} onClick={() => saveSummary(draft.trim())}>Uložiť</Button>
                <Button variant="ghost" disabled={busy} onClick={() => setEditSummary(false)}>Zrušiť</Button>
                {d.summary_edited && (
                  <Button variant="ghost" disabled={busy} onClick={() => saveSummary("")}>Vrátiť AI verziu</Button>
                )}
              </div>
            </div>
          ) : (d.summary || d.summary_edited) ? (
            <div style={{ ...st.card, background: "var(--surface)", marginTop: 12 }}>
              <div style={{ fontStyle: "italic", fontFamily: "var(--font-display)", fontSize: 15, lineHeight: 1.5 }}>{d.summary}</div>
              <div style={{ display: "flex", alignItems: "center", gap: 10, marginTop: 10, flexWrap: "wrap" }}>
                <Button variant="ghost" onClick={() => { setDraft(d.summary || ""); setEditSummary(true); }}>Upraviť</Button>
                {/* keď je súhrn tvoj, AI ho neprepisuje — nech je to vidieť, inak by pôsobil ako zaseknutý */}
                {d.summary_edited && <Caption variant="note">Tvoja verzia — AI ju neprepisuje.</Caption>}
              </div>
            </div>
          ) : (
            <div style={{ marginTop: 12 }}>
              <Button variant="ghost" onClick={() => { setDraft(""); setEditSummary(true); }}>Napísať súhrn sám/sama</Button>
            </div>
          )}
          <SectionHeader label="Fakty" />
          {(d.facts || []).length === 0 ? (
            <div style={st.taskDesc}>{busy ? "Učím sa ťa spoznať z tvojich dát…" : "Zatiaľ nič — pridaj fakt nižšie alebo pamäť dorastie z tvojej aktivity a nálad."}</div>
          ) : (
            <div style={st.taskList}>
              {d.facts.map((f) => (
                <div key={f.id} className="task" style={st.task}>
                  <span style={st.taskText}>
                    <span style={st.taskLabel}>{f.text}</span>
                    <span style={st.taskDesc}>{f.type === "manual" ? "tvoj fakt" : f.type} · dôvera {Math.round((f.conf || 0) * 100)}%</span>
                  </span>
                  <button onClick={() => delFact(f.id)} style={st.del} title="Zabudnúť"><Icon name="close" size={16} color="currentColor" /></button>
                </div>
              ))}
            </div>
          )}
          <div style={{ display: "flex", gap: 8, marginTop: 10, alignItems: "stretch" }}>
            <input className="field" style={{ ...st.field, flex: 1 }} value={newFact} maxLength={160}
              placeholder="Pridaj fakt o sebe (napr. som ranné vtáča)" onChange={(e) => setNewFact(e.target.value)}
              onKeyDown={(e) => { if (e.key === "Enter") addFact(); }} />
            <Button variant="primary" onClick={addFact} disabled={busy || !newFact.trim()}>Pridať</Button>
          </div>
          {(d.facts || []).length > 0 && (
            <Button variant="destructive" block style={{ marginTop: 16 }} onClick={wipe}><Icon name="delete" size={16} color="currentColor" />Vymazať celú pamäť</Button>
          )}
        </>)}
      </>)}
    </Modal>
  );
}
export function ProfilHub({ s, user, focus, onFocusHandled, onClose, onLogout, reload, onTour, onTutorial, onOpenCommand, onHelp, onContact, onChangelog }) {
  const [showLevels, setShowLevels] = useState(false);
  const [showShop, setShowShop] = useState(false);
  const [shopFocus, setShopFocus] = useState(null);
  // v2 — deep-link from the "enough points" notification opens the shop at that item
  useEffect(() => {
    if (focus && focus.startsWith("shop")) { setShopFocus(focus.split(":")[1] || null); setShowShop(true); onFocusHandled && onFocusHandled(); }
  }, [focus]);  // eslint-disable-line react-hooks/exhaustive-deps
  // v33h — deep-link z „Viac → Nastavenia": doscrolluj na nadpis Nastavenia.
  // onFocusHandled AŽ po scrolle: keby sme ho volali hneď, focus by sa zmenil na null,
  // efekt by sa cleanupol a clearTimeout by zrušil scroll (fungovalo len prvýkrát).
  useEffect(() => {
    if (focus !== "settings") return undefined;
    const t = setTimeout(() => {
      const el = document.querySelector('[data-sect="settings"]');
      if (el) { try { el.scrollIntoView({ behavior: "smooth", block: "start" }); } catch { /* */ } }
      onFocusHandled && onFocusHandled();
    }, 60);
    return () => clearTimeout(t);
  }, [focus]);  // eslint-disable-line react-hooks/exhaustive-deps
  const [showMemory, setShowMemory] = useState(false);  // v8 — AI memory (Čo si o tebe pamätám)
  const [showRetention, setShowRetention] = useState(false);  // ako dlho držíme AI rozhovory
  const retention = (s.user && s.user.ai_chat_retention) ?? 90;
  // v32.17 — easter egg: 5 rýchlych ťuknutí na verziu → odznak Pátrač 🥚 (prevzaté z bočného menu)
  const eggTaps = useRef({ n: 0, t: 0 });
  const [eggShow, setEggShow] = useState(false);
  async function eggTap() {
    const now = Date.now();
    if (now - eggTaps.current.t > 2500) eggTaps.current.n = 0;
    eggTaps.current = { n: eggTaps.current.n + 1, t: now };
    if (eggTaps.current.n >= 5) {
      eggTaps.current.n = 0; setEggShow(true); setTimeout(() => setEggShow(false), 2500);
      try { await api("/api/easter-egg", { method: "POST", body: { code: "verzia" } }); reload(); } catch { /* */ }
    }
  }
  // v25 §6.2 — Lite vždy prestane počítať body (voľba „len skryť“ zanikla)
  // v33h — pri prepnutí Lite sa skryje/objaví sekcia „Ďalšie" NAD prepínačom → obsah by poskočil;
  // zapamätáme si pozíciu prepínača vo viewporte a po re-renderi ju obnovíme (kotva zostane na mieste).
  const liteAnchorRef = useRef(null);
  const liteScrollFix = useRef(null);
  useLayoutEffect(() => {
    if (liteScrollFix.current == null) return;
    const el = liteAnchorRef.current;
    if (el) window.scrollBy(0, el.getBoundingClientRect().top - liteScrollFix.current);
    liteScrollFix.current = null;
  }, [s.lite_mode]);
  async function applyLite(on) {
    const el = liteAnchorRef.current;
    liteScrollFix.current = el ? el.getBoundingClientRect().top : null;
    await api("/api/profile/lite", { method: "POST", body: { lite: on } });
    if (on && session.character === "dodo" && confirm("V Lite režime sa hodí jemnejší tón. Prepnúť AI na Ňaňu?")) {
      await api("/api/profile/character", { method: "POST", body: { character: "nana" } });
      setSession({ ...session, character: "nana", characterName: "Ňaňa", assistantGender: "z" });
    }
    reload();
  }
  const [badgeInfo, setBadgeInfo] = useState(null);  // PR5: badge explanation modal
  const [showNotifPrefs, setShowNotifPrefs] = useState(false);  // PR6: notification prefs modal
  const [showInstall, setShowInstall] = useState(false);        // install-to-home-screen guide
  const [reportBusy, setReportBusy] = useState(false);
  const [theme, setThemeState] = useState(getAppTheme());  // v8 colour theme
  const fileRef = useRef(null);  // avatar upload (moved here from the public profile)
  const themeUnlocked = (k) => (s.free_themes || ["green", "emerald", "jade", "cobalt", "lime", "cyber", "ultraviolet"]).includes(k) || (s.inventory || []).includes("color_" + k);
  async function pickTheme(t) {
    if (!themeUnlocked(t)) { previewAppTheme(t, theme, 3000); return; }  // zamknuté → len ukážka na 3 s
    setThemeState(t); applyAppTheme(t);
    // v32.21 — po uložení refetchni stav, nech s.theme nie je zastaraný; inak sync-effect nižšie
    // pri návrate na Účet prepíše picker späť na starú (serverom ešte nenačítanú) farbu.
    try { await api("/api/profile/theme", { method: "POST", body: { theme: t } }); reload(); } catch { /* offline ok */ }
  }
  async function uploadAvatar(e) {
    const f = e.target.files[0];
    e.target.value = "";  // nech sa dá tá istá fotka skúsiť znova (change sa inak nespustí)
    if (!f) return;
    // Fotka z mobilu má 3–5 MB a narazila by na limit requestu; server ju aj tak
    // prekóduje na 256 px. Zmenšíme ju už tu — vyrieši to aj HEIC z iPhonu.
    let upload = f;
    try { upload = await shrinkImage(f, { maxEdge: 512 }); }
    catch { /* prehliadač to nezvládol → pošli originál, server si poradí */ }
    const fd = new FormData(); fd.append("file", upload);
    try {
      const res = await fetch("/api/profile/avatar", { method: "POST", body: fd });
      const d = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(d.error || "Chyba pri nahrávaní.");
      reload();
    } catch (err) { alert(err.message); }
  }
  // v8 — keep the picker in sync with the server-saved theme.
  useEffect(() => { if (s.theme) setThemeState(s.theme); }, [s.theme]);
  async function downloadReport() {
    if (!session.premium) { alert("Mesačný report je súčasťou Community. 💛"); return; }  // v13 C4
    setReportBusy(true);
    try {
      const res = await fetch("/api/report.pdf");
      if (res.status === 429) { const j = await res.json().catch(() => ({})); alert(j.error || "Report si dnes už stiahol. Skús zajtra."); }
      else if (res.status === 402) { alert("Mesačný report je súčasťou Community. 💛"); }
      else if (!res.ok) { alert("Report sa nepodarilo vytvoriť."); }
      else {
        const url = URL.createObjectURL(await res.blob());
        const a = document.createElement("a"); a.href = url; a.download = "buzerlistok-report.pdf"; a.click();
        URL.revokeObjectURL(url);
      }
    } catch (e) { /* */ }
    setReportBusy(false);
  }
  async function downloadData() {
    try {
      const res = await fetch("/api/account/export");
      if (!res.ok) { alert("Export sa nepodaril."); return; }
      const url = URL.createObjectURL(await res.blob());
      const a = document.createElement("a"); a.href = url; a.download = "buzerlistok-moje-data.json"; a.click();
      URL.revokeObjectURL(url);
    } catch (e) { alert("Export sa nepodaril."); }
  }
  async function deleteAccount() {
    if (!confirm("Naozaj zmazať účet? Vymažú sa VŠETKY tvoje dáta — návyky, body, priatelia, správy. Nedá sa to vrátiť.")) return;
    if (!confirm("Posledné varovanie: účet a všetky dáta budú natrvalo zmazané. Pokračovať?")) return;
    try {
      const res = await fetch("/api/account", { method: "DELETE" });
      if (res.ok) { window.location.href = "/"; } else alert("Účet sa nepodarilo zmazať.");
    } catch (e) { alert("Účet sa nepodarilo zmazať."); }
  }
  async function goPremium() {
    try { const r = await api("/api/billing/checkout", { method: "POST" }); if (r.url) window.location.href = r.url; }
    catch (e) { alert(e.message || "Platby zatiaľ nie sú dostupné."); }
  }
  async function goPortal() {
    try { const r = await api("/api/billing/portal", { method: "POST" }); if (r.url) window.location.href = r.url; }
    catch (e) { alert(e.message || "Nepodarilo sa otvoriť správu predplatného."); }
  }
  const [customOpen, setCustomOpen] = useState(false);   // v13 A5 — custom assistant editor
  const [customName, setCustomName] = useState(user.ai_custom_name || "");
  const [customPersona, setCustomPersona] = useState(user.ai_custom_persona || "");
  const [customGender, setCustomGender] = useState(user.ai_custom_gender || "z");  // v26.3 §19 — rod persóny
  const [customBusy, setCustomBusy] = useState(false);
  const [customErr, setCustomErr] = useState("");
  async function pickCharacter(k, name, g) {
    if (k !== "nana" && !session.premium) { alert("Tento asistent je súčasťou Community. 💛"); return; }
    if (k === "custom") { setCustomErr(""); setCustomOpen(true); return; }
    try { await api("/api/profile/character", { method: "POST", body: { character: k } });
      // v32 — rod postavy sa premietne hneď (texty typu „Skôr som si všimla"); autoritatívnu
      // hodnotu aj tak prinesie najbližšie /api/me ako `ai_assistant_gender`
      session.character = k; session.characterName = name; setSession({ assistantGender: g }); reload(); }
    catch (e) { alert("Nepodarilo sa zmeniť postavu."); }
  }
  async function saveCustom() {
    if (!customName.trim()) { setCustomErr("Zadaj meno asistenta."); return; }
    if (!customPersona.trim()) { setCustomErr("Napíš, ako sa má správať."); return; }
    setCustomBusy(true); setCustomErr("");
    try {
      const r = await api("/api/profile/character", { method: "POST", body: { character: "custom", custom_name: customName.trim(), custom_persona: customPersona.trim(), custom_gender: customGender } });
      session.character = "custom"; session.characterName = r.name || customName.trim();
      setSession({ assistantGender: customGender });   // v32 — rod vlastnej persóny hneď aj vo FE textoch
      setCustomOpen(false); reload();
    } catch (e) { setCustomErr(e.message); }
    setCustomBusy(false);
  }
  const [badges, setBadges] = useState([]);
  const [statusText, setStatusText] = useState("");
  const [statusIcon, setStatusIcon] = useState("");
  const [statusIconPick, setStatusIconPick] = useState(false);  // v9 BuzerIcon picker
  const [statusBusy, setStatusBusy] = useState(false);
  const [statusSaved, setStatusSaved] = useState(false);
  const statusRef = useRef(null);   // auto-grow: rastie do max-height v .bz-status-field, potom scrolluje
  function growStatus(el) { if (!el) return; el.style.height = "auto"; el.style.height = Math.min(el.scrollHeight, 148) + "px"; }
  useEffect(() => { growStatus(statusRef.current); }, [statusText]);
  const [editName, setEditName] = useState(false);   // inline úprava mena v hlavičke
  const { open: openPublicProfile } = useProfileModal();  // v32.21 — globálny modal verejného profilu
  const [nameVal, setNameVal] = useState("");
  const [genderVal, setGenderVal] = useState("z");  // v32.18 — pohlavie účtu (ceruzka)
  const [nameBusy, setNameBusy] = useState(false);
  useEffect(() => { api("/api/users/" + user.id + "/profile").then((d) => { setBadges(d.badges || []); setStatusText(d.status || ""); setStatusIcon(d.status_icon || ""); }).catch(() => {}); }, [user.id]);
  async function saveStatus() {
    setStatusBusy(true);
    try { await api("/api/profile/status", { method: "POST", body: { status: statusText, icon: statusIcon } }); setStatusSaved(true); setTimeout(() => setStatusSaved(false), 2000); }
    catch (e) { /* */ }
    setStatusBusy(false);
  }
  const me = s.user || user;
  function startEditName() { setNameVal(me.name || ""); setGenderVal(["m", "x"].includes(me.gender) ? me.gender : "z"); setEditName(true); }
  async function saveName() {
    const v = nameVal.trim();
    if (!v) { setEditName(false); return; }
    setNameBusy(true);
    try { await api("/api/profile/name", { method: "POST", body: { name: v, gender: genderVal } }); await reload(); setEditName(false); }
    catch (e) { /* */ }
    setNameBusy(false);
  }
  const pts = s.totalPoints;
  const level = getLevel(pts);
  const next = nextLevel(pts);
  return (
    <>
      {/* PR1/PR2 — no "Späť" button, no "Môj profil" heading (the bottom nav handles navigation) */}
      <div style={{ ...st.profHead, marginTop: 4 }}>
        <div style={{ position: "relative", flexShrink: 0 }}>
          <Avatar user={me} size={68} onClick={() => fileRef.current && fileRef.current.click()} />
          <button onClick={() => fileRef.current && fileRef.current.click()} style={{ ...st.avatarEdit, width: 26, height: 26 }} title="Zmeniť fotku"><BuzerIcon name="camera" size={14} color="#161512" /></button>
          {/* accept="image/*" — HEIC z iPhonu už nemusíme odmietať, uploadAvatar ho prekóduje na JPEG */}
          <input ref={fileRef} type="file" accept="image/*" onChange={uploadAvatar} style={{ display: "none" }} />
        </div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={st.profName}>
            {editName ? (
              <span style={{ display: "inline-flex", flexDirection: "column", gap: 8, alignItems: "flex-start" }}>
                <span style={{ display: "inline-flex", gap: 6, alignItems: "center", flexWrap: "wrap" }}>
                  <input className="field" autoFocus maxLength={60} value={nameVal} onChange={(e) => setNameVal(e.target.value)}
                    onKeyDown={(e) => { if (e.key === "Enter") saveName(); if (e.key === "Escape") setEditName(false); }}
                    style={{ ...st.field, padding: "6px 10px", fontSize: 17, width: 170, minWidth: 0 }} />
                  {/* v32 — zjednotené: zelená fajka + X ako modálový icon button (ghost = biele pozadie) */}
                  <IconButton variant="ghost" size="sm" onClick={saveName} disabled={nameBusy} title="Uložiť" aria-label="Uložiť" style={{ color: "var(--success)" }}>
                    {nameBusy ? "…" : <Icon name="check" size={16} color="currentColor" stroke={2.6} />}
                  </IconButton>
                  <IconButton variant="ghost" size="sm" onClick={() => setEditName(false)} title="Zrušiť" aria-label="Zrušiť">
                    <Icon name="close" size={16} color="currentColor" />
                  </IconButton>
                </span>
                {/* v32.18 — úprava pohlavia účtu (ovplyvňuje rodové oslovenie v citátoch a AI) */}
                <span style={{ display: "inline-flex", alignItems: "center", gap: 8 }}>
                  <span style={st.sheetDesc}>Pohlavie</span>
                  <Segmented options={[{ value: "z", label: "Žena" }, { value: "m", label: "Muž" }, { value: "x", label: "Iné" }]} value={genderVal} onChange={setGenderVal} />
                </span>
              </span>
            ) : (
              <>
                {me.name || "Bez mena"}
                {/* v32 — ceruzka v krúžku (globálny icon button ako oko v Dnes) */}
                <IconButton variant="neutral" size="sm" onClick={startEditName} title="Zmeniť meno" aria-label="Zmeniť meno"
                  style={{ verticalAlign: "middle", marginLeft: 8 }}>
                  <BuzerIcon name="pencil" size={15} color="currentColor" />
                </IconButton>
              </>
            )}
          </div>
          {/* v20 §22 — označenie plánu vo farbe témy, pod menom (ako verejný profil) */}
          {!editName && <div style={{ marginTop: 4 }}><Tag tone="brand" icon="star2">{session.premium ? "Community" : "Free"}</Tag></div>}
        </div>
      </div>

      {/* v32 — plnošírkové primary tlačidlo namiesto textového linku; otvára zjednotený profilový modal */}
      <Button variant="primary" block style={{ marginTop: 12 }} onClick={() => openPublicProfile(user.id)}>
        <BuzerIcon name="eye" size={17} color="currentColor" /> Zobraziť verejný profil
      </Button>

      {/* PR1 — status editor lives here (read-only on the public profile).
          v33 — celý status editor zlúčený do JEDNEJ zaoblenej dlaždice (kruhová ikona · text ·
          Uložiť) namiesto rozhádzaného radu poľ. Mäkké neutrálne pozadie (--neutral-surface),
          funguje vo všetkých témach aj dark mode cez tokeny. */}
      <div style={{ ...st.progressHead, marginTop: 16 }}><span className="bz-subhead">Status</span></div>
      <div style={{ display: "flex", alignItems: "flex-start", gap: 10, padding: 8,
        background: "var(--neutral-surface)", border: "1px solid var(--border)",
        borderRadius: "var(--radius-lg)", boxShadow: "var(--shadow-sm)" }}>
        <button type="button" onClick={() => setStatusIconPick(true)} aria-label="Vyber ikonu statusu"
          style={{ flex: "none", width: 46, height: 46, borderRadius: "var(--radius-pill)",
            background: "var(--surface-solid)", border: "1px solid var(--border)",
            display: "flex", alignItems: "center", justifyContent: "center", cursor: "pointer", color: "var(--text)" }}>
          <Glyph icon={statusIcon || "smile"} size={22} />
        </button>
        <textarea ref={statusRef} className="bz-status-field" rows={1}
          maxLength={200} value={statusText} onChange={(e) => setStatusText(e.target.value)} placeholder="Napíš svoj status…" />
        {/* Uložiť cez globálne primary tlačidlo (brand/zelené); po uložení krátko potvrdí fajkou */}
        <Button variant="primary" onClick={saveStatus} disabled={statusBusy} style={{ flex: "none" }}>
          {statusBusy ? "…" : statusSaved
            ? <span style={{ display: "inline-flex", alignItems: "center", gap: 4 }}><Icon name="check" size={15} color="currentColor" stroke={2.6} />Uložené</span>
            : "Uložiť"}
        </Button>
      </div>
      {statusIconPick && (
        <Modal title="Vyber ikonu" icon="palette" onClose={() => setStatusIconPick(false)} maxWidth={420}>
          <IconPicker value={statusIcon} onPick={(nm) => { setStatusIcon(nm); setStatusIconPick(false); }} />
        </Modal>
      )}

      {/* v20 §19/§20 — level box, štatistiky a odznaky presunuté do Prehľad; Profil preusporiadaný:
          head → Status → AI asistent → Ďalšie → Nastavenia (zlúčené) → Súkromie a dáta */}

      {/* AI asistent */}
      <div style={{ ...st.progressHead, marginTop: 18 }}><span className="bz-subhead">AI asistent</span></div>
      <div style={st.taskDesc}>Kto ťa bude motivovať, koučovať a odpovedať v chate.</div>
      {/* v18 §12 — own GPU layer (Redmi/Mali compositing glitch on the card row) */}
      <div style={{ display: "flex", gap: 10, marginTop: 8, transform: "translateZ(0)", backfaceVisibility: "hidden" }}>
        {/* `g` = rod postavy (ako o SEBE hovorí) — zrkadlí `ai.CHARACTERS[*].gender` na serveri */}
        {[{ k: "nana", n: "Ňaňa", g: "z", e: "flower", d: "láskavá, povzbudzujúca" },
          { k: "dodo", n: "Ďoďo", g: "m", e: "dumbbell", d: "priamy hecér" },
          { k: "custom", n: s.ai_character === "custom" ? (session.characterName || "Vlastný") : "Vlastný", g: customGender, e: "ai", d: "podľa teba" }].map((c) => {
          const locked = c.k !== "nana" && !session.premium;
          return (
            <button key={c.k} onClick={() => pickCharacter(c.k, c.n, c.g)}
              style={{ ...st.statCard, background: "var(--surface)", ...(s.ai_character === c.k ? st.streakCard : {}), cursor: "pointer", gap: 2, position: "relative", opacity: locked ? 0.6 : 1 }}>
              {locked && <span style={{ position: "absolute", top: 6, right: 6 }}><Icon name="lock" size={13} color="var(--muted)" /></span>}
              <div style={{ fontSize: 26 }}><BuzerIcon name={c.e} size={26} color="var(--brand-primary)" /></div>
              <div className="display" style={{ fontSize: 16, fontWeight: 700, color: s.ai_character === c.k ? "var(--ember)" : "var(--text)" }}>{c.n}</div>
              <div style={{ ...st.statLabel, fontFamily: "var(--ff-ui)" }}>{c.d}</div>
            </button>
          );
        })}
      </div>
      {customOpen && (
        <Modal center title="Vlastný asistent" icon="ai" onClose={() => setCustomOpen(false)} maxWidth={440}>
          <div style={st.taskDesc}>Daj svojmu asistentovi meno a napíš, ako sa má správať a akým tónom ti písať.</div>
          <label className="bz-field__label" style={{ marginTop: 10, display: "block" }}>Meno *</label>
          <input className="field" style={st.field} value={customName} maxLength={30} autoFocus
            placeholder="napr. Coach Max, Babka, Sensei…" onChange={(e) => setCustomName(e.target.value)} />
          <label className="bz-field__label" style={{ marginTop: 10, display: "block" }}>Ako sa má správať *</label>
          <textarea className="field" rows={4} value={customPersona} maxLength={600}
            style={{ ...st.field, resize: "none", lineHeight: 1.4 }}
            placeholder="napr. Si pokojný mentor. Píšeš krátko, vecne, bez emócií. Občas pridáš citát o disciplíne."
            onChange={(e) => setCustomPersona(e.target.value)} />
          <div style={{ ...st.fieldHint, textAlign: "right" }}>{customPersona.length}/600</div>
          {/* v26.3 §19 — rod persóny určuje skloňovanie rozprávača v starostlivom check-ine */}
          <label className="bz-field__label" style={{ marginTop: 6, display: "block" }}>Rod asistenta (kvôli oslovovaniu)</label>
          <div style={{ display: "flex", gap: 8, marginTop: 6 }}>
            {[["z", "Žena"], ["m", "Muž"]].map(([g, lbl]) => (
              <button key={g} type="button" onClick={() => setCustomGender(g)}
                style={{ flex: 1, padding: "9px 0", borderRadius: 10, cursor: "pointer",
                  border: customGender === g ? "1.5px solid var(--brand-primary)" : "1px solid var(--border)",
                  background: customGender === g ? "var(--brand-primary-surface)" : "var(--surface)",
                  color: customGender === g ? "var(--brand-primary)" : "var(--text2)",
                  fontWeight: customGender === g ? 700 : 500 }}>{lbl}</button>
            ))}
          </div>
          {customErr && <div style={st.err}>{customErr}</div>}
          <button type="button" onClick={saveCustom} disabled={customBusy || !customName.trim() || !customPersona.trim()}
            style={{ ...st.primaryBtn, width: "100%", marginTop: 8, opacity: (customBusy || !customName.trim() || !customPersona.trim()) ? 0.6 : 1 }}>
            {customBusy ? "ukladám…" : "Uložiť a aktivovať"}</button>
        </Modal>
      )}
      {session.ai && (
        <div style={{ marginTop: 10 }}>
        <ProfilRow icon={<BuzerIcon name="ai" size={22} color="var(--text2)" />} label="Čo si o tebe pamätám"
          hint={session.premium ? "›" : <Icon name="lock" size={14} color="var(--muted)" />}
          onClick={() => session.premium ? setShowMemory(true) : alert("Pamäť AI je súčasťou Community. 💛")} />
        </div>
      )}
      {showMemory && <AiMemoryModal onClose={() => setShowMemory(false)} />}

      {/* Ďalšie — v33i (task #8): v Lite je táto sekcia prázdna (Obchod aj Report sú PRO-only),
          takže nadpis sa nezobrazuje (nič pod ním by inak nedávalo zmysel). */}
      {!s.lite_mode && <div style={{ ...st.progressHead, marginTop: 18 }}><span className="bz-subhead">Ďalšie</span></div>}
      {!s.lite_mode && <ProfilRow icon={<Icon name="shop" size={22} color="var(--text2)" />} label="Obchod za body" hint={(s.spendable_points || 0) + " b."} onClick={() => setShowShop(true)} />}
      {!s.lite_mode && <ProfilRow icon={<Icon name="report" size={22} color="var(--text2)" />} label="Mesačný report (PDF)" hint={reportBusy ? "…" : (session.premium ? "📥" : <Icon name="lock" size={14} color="var(--muted)" />)} onClick={downloadReport} />}
      {showShop && <ShopSheet s={s} focus={shopFocus} onClose={() => { setShowShop(false); setShopFocus(null); }} onBought={reload} />}

      {/* Nastavenia — zlúčené (Nastavenia + Appka a upozornenia + Predplatné), Odhlásiť sa posledné */}
      {/* v33h — data-sect = kotva pre deep-link z „Viac → Nastavenia" (bez pozadia/rámika) */}
      <div data-sect="settings" style={{ ...st.progressHead, marginTop: 18, scrollMarginTop: 12 }}>
        <span className="bz-subhead">Nastavenia</span></div>
      {/* v8 Pilier 3 — Lite režim toggle */}
      <div ref={liteAnchorRef} style={{ ...st.profRow, cursor: "default", scrollMarginTop: 12 }}>
        <span className="bz-lead bz-lead--circle"><BuzerIcon name="leaf" size={20} color="var(--text2)" /></span>
        <span style={{ flex: 1, textAlign: "left", minWidth: 0 }}>
          <span style={st.sheetLabel}>Lite režim</span>
          <span style={st.sheetDesc}>{s.lite_mode ? "Zapnutý — body sa nepočítajú, sleduje sa len denná séria." : "V Lite sa body nepočítajú — sleduje sa len denná séria."}</span>
        </span>
        <button type="button" role="switch" aria-checked={!!s.lite_mode}
          onClick={() => applyLite(!s.lite_mode)}
          title="Zapnúť/vypnúť Lite režim"
          style={{ ...st.switch, ...(s.lite_mode ? st.switchOn : {}), flex: "none" }}>
          <span style={{ ...st.switchKnob, ...(s.lite_mode ? st.switchKnobOn : {}) }} />
        </button>
      </div>
      {/* v33 §11 — porovnávanie s predchádzajúcim kolom. Vypnutie je len o ZOBRAZENÍ:
          body a odznaky za prekonanie chodia ďalej, aby preferencia nikoho nestála body. */}
      {!s.lite_mode && (
        <div style={{ ...st.profRow, cursor: "default" }}>
          <span className="bz-lead bz-lead--circle"><BuzerIcon name="undo" size={20} color="var(--text2)" /></span>
          <span style={{ flex: 1, textAlign: "left", minWidth: 0 }}>
            <span style={st.sheetLabel}>Porovnávať s minulým kolom</span>
            <span style={st.sheetDesc}>Pri opakovaných výzvach a súťažiach uvidíš, ako ti to išlo minule a čo treba prekonať.</span>
          </span>
          <button type="button" role="switch" aria-checked={s.rounds_compare !== false}
            onClick={async () => { await api("/api/profile/rounds-compare", { method: "POST", body: { on: s.rounds_compare === false } }); reload(); }}
            title="Zapnúť/vypnúť porovnávanie s minulým kolom"
            style={{ ...st.switch, ...(s.rounds_compare !== false ? st.switchOn : {}), flex: "none" }}>
            <span style={{ ...st.switchKnob, ...(s.rounds_compare !== false ? st.switchKnobOn : {}) }} />
          </button>
        </div>
      )}
      {/* DENNÍK §5.5 — riadok „Na tento deň" (zápis spred roka/mesiaca/týždňa) sa dá vypnúť */}
      <div style={{ ...st.profRow, cursor: "default" }}>
        <span className="bz-lead bz-lead--circle"><BuzerIcon name="clock" size={20} color="var(--text2)" /></span>
        <span style={{ flex: 1, textAlign: "left", minWidth: 0 }}>
          <span style={st.sheetLabel}>Ukazovať „Na tento deň"</span>
          <span style={st.sheetDesc}>V denníku dňa ti pripomenie, čo si písal/a pred rokom, mesiacom či týždňom.</span>
        </span>
        <button type="button" role="switch" aria-checked={s.journal_throwback_on !== false}
          onClick={async () => { await api("/api/journal/throwback", { method: "POST", body: { on: s.journal_throwback_on === false } }); reload(); }}
          title={"Zapnúť/vypnúť „Na tento deň“"}
          style={{ ...st.switch, ...(s.journal_throwback_on !== false ? st.switchOn : {}), flex: "none" }}>
          <span style={{ ...st.switchKnob, ...(s.journal_throwback_on !== false ? st.switchKnobOn : {}) }} />
        </button>
      </div>
      {/* farba aplikácie (theme picker) */}
      <div style={{ ...st.profRow, cursor: "default" }}>
        <span className="bz-lead bz-lead--circle"><Icon name="settings" size={20} color="var(--text2)" /></span>
        <span style={{ flex: 1, textAlign: "left", minWidth: 0 }}>
          <span style={st.sheetLabel}>Farba aplikácie</span>
          <span style={st.sheetDesc}>Vyber si tému.</span>
        </span>
        <span style={{ display: "flex", flexWrap: "wrap", gap: 8, flex: "none", justifyContent: "flex-end", maxWidth: 156 }}>
          {APP_THEME_SWATCHES.filter(([k]) => k !== "indigo").map(([k, col]) => {
            const locked = !themeUnlocked(k);
            return (
              <button key={k} type="button" onClick={() => pickTheme(k)} title={locked ? k + " (zamknuté — odomkni v obchode)" : k} aria-label={k}
                style={{ width: 30, height: 30, borderRadius: "50%", background: col, cursor: "pointer",
                  border: theme === k ? "none" : "2px solid var(--border)", opacity: locked ? 0.55 : 1,
                  display: "flex", alignItems: "center", justifyContent: "center" }}>
                {theme === k ? <Icon name="check" size={14} color="#fff" stroke={3} /> : (locked && <Icon name="lock" size={12} color="#fff" />)}
              </button>
            );
          })}
        </span>
      </div>
      <ProfilRow icon={<Icon name="bell" size={22} color="var(--text2)" />} label="Upozornenia" hint="›" onClick={() => setShowNotifPrefs(true)} />
      <ProfilRow icon={<BuzerIcon name="phone" size={22} color="var(--text2)" />} label="Inštalovať appku" onClick={() => setShowInstall(true)} />
      {/* v33h — „Spustiť tutoriál" DOČASNE SKRYTÉ (revert: odkomentuj riadok nižšie)
      {!s.lite_mode && <ProfilRow icon={<BuzerIcon name="ai" size={22} color="var(--text2)" />} label="Spustiť tutoriál" hint="›" onClick={() => onTutorial && onTutorial()} />}
      */}
      {showNotifPrefs && <NotifPrefsModal onClose={() => setShowNotifPrefs(false)} />}
      {showInstall && <InstallGuide onClose={() => setShowInstall(false)} />}
      {/* v36 — Community má každý natrvalo, skúšobné obdobie zaniklo. Preto sa už neponúka
          „Predplatiť/spravovať" každému: správa predplatného má zmysel len pre účet so skutočným
          Stripe zákazníkom (inak portál vrátil „Žiadne predplatné"), ostatní vidia len vetu
          o tom, dokedy Community majú. */}
      {session.has_billing
        ? <ProfilRow icon={<Icon name="crown" size={22} color="var(--text2)" />} label="Predplatné — spravovať" onClick={goPortal} />
        : session.premium
          ? <div style={{ ...st.taskDesc, padding: "2px 6px 10px" }}>
              {session.plan_until
                ? <>Community máš do {fmtDuePretty(session.plan_until)}.</>
                : <>Community máš natrvalo — bez obmedzenia.</>}
            </div>
          : <ProfilRow icon={<Icon name="crown" size={22} color="var(--text2)" />} label="Prejsť na Community" hint="›" onClick={goPremium} />}
      <ProfilRow icon={<Icon name="logout" size={22} color="var(--text2)" />} label="Odhlásiť sa" onClick={onLogout} />

      {/* v32.17 — Pomoc (prevzaté z bočného menu, ktoré nahradila spodná navigácia) */}
      <div style={{ ...st.progressHead, marginTop: 18 }}><span className="bz-subhead">Pomoc</span></div>
      <ProfilRow icon={<BuzerIcon name="bookopen" size={22} color="var(--text2)" />} label="Pomoc a časté otázky" hint="›" onClick={onHelp} />
      <ProfilRow icon={<BuzerIcon name="mail" size={22} color="var(--text2)" />} label="Napísať nám / nahlásiť" hint="›" onClick={onContact} />
      <ProfilRow icon={<BuzerIcon name="sparkles" size={22} color="var(--text2)" />}
        label={eggShow ? "Pátrač! Našiel/a si tajomstvo. 🥚" : "Čo je nové"}
        hint={"verzia " + verLabel(__APP_VERSION__)} onClick={() => { eggTap(); onChangelog && onChangelog(); }} />

      <div style={{ ...st.progressHead, marginTop: 18 }}><span className="bz-subhead">Súkromie a dáta</span></div>
      {session.ai && (
        <ProfilRow icon={<BuzerIcon name="ai" size={22} color="var(--text2)" />} label="Uchovávanie AI rozhovorov"
          hint={chatRetentionLabel(retention)} onClick={() => setShowRetention(true)} />
      )}
      {showRetention && <ChatRetentionModal value={retention} reload={reload} onClose={() => setShowRetention(false)} />}
      <ProfilRow icon={<Icon name="report" size={22} color="var(--text2)" />} label="Stiahnuť moje dáta" hint="JSON" onClick={downloadData} />
      <button onClick={deleteAccount} className="sheet-row" style={{ ...st.profRow, cursor: "pointer" }}>
        <span className="bz-lead bz-lead--circle"><Icon name="delete" size={22} color="var(--danger)" /></span>
        <span style={{ flex: 1, textAlign: "left" }}>
          <span style={{ ...st.sheetLabel, color: "var(--danger)" }}>Zmazať účet</span>
          <span style={st.sheetDesc}>Natrvalo vymaže účet a všetky dáta (GDPR).</span>
        </span>
      </button>
    </>
  );
}
export function ShopSheet({ s, focus, onClose, onBought }) {
  const [data, setData] = useState(null);
  const [hl, setHl] = useState(null);  // v2 — highlight the deep-linked item
  const [busy, setBusy] = useState("");
  const [err, setErr] = useState("");
  const [activeTheme, setActiveTheme] = useState(() => getAppTheme());
  const [activeFont, setActiveFont] = useState(() => getAppFont());
  // v32 — SKÚŠOBŇA ozdôb: čisto klientská ukážka ozdoby na SKUTOČNEJ profilovke.
  // {code, slot, name} berieme z položky katalógu (slot je údaj backendu, nehádame ho z kódu).
  // Nič sa nekupuje ani neukladá — zavretím Obchodu stav zaniká spolu s komponentom.
  // Spúšťa sa ŤUKNUTÍM NA CELÝ RIADOK ozdoby (nie vlastným tlačidlom — to by zúžilo text).
  const [tryOn, setTryOn] = useState(null);
  const [shopBurst, setShopBurst] = useState(null);  // ukážka animácie „pri otvorení appky" (padá zhora)
  // f-batch — ukážka animácie „po splnení návyku": ROVNAKÝ rozpŕskavajúci efekt ako v appke,
  // vychádza z bodu ťuku (tlačidlo „Skús"), nikdy nepadá zhora. {key,effect,rect}.
  const [doneBurst, setDoneBurst] = useState(null);
  useEffect(() => {
    if (!focus || !data) return;
    const el = document.querySelector(`[data-shopcode="${focus}"]`);
    if (el) { el.scrollIntoView({ behavior: "smooth", block: "center" }); setHl(focus); const t = setTimeout(() => setHl(null), 2200); return () => clearTimeout(t); }
  }, [focus, data]);
  const load = useCallback(async () => { try { setData(await api("/api/shop")); } catch (e) { /* */ } }, []);
  useEffect(() => { load(); }, [load]);
  async function buy(code) {
    setBusy(code); setErr("");
    try { await api("/api/shop/buy", { method: "POST", body: { code } }); await load(); onBought && onBought(); }
    catch (e) { setErr(e.message); }
    setBusy("");
  }
  async function equip(code, on) {
    // Ukážka a skutočné nasadenie si nesmú odporovať: len čo si človek niečo naozaj nasadí
    // (alebo zloží), skúšobňa sa vypne a ukazuje zase reálny stav z `s.user`.
    setTryOn(null);
    try { await api("/api/shop/equip", { method: "POST", body: { code, on } }); onBought && onBought(); }
    catch (e) { setErr(e.message); }
  }
  async function applyColor(theme) {
    try { await api("/api/profile/theme", { method: "POST", body: { theme } }); applyAppTheme(theme); setActiveTheme(theme); onBought && onBought(); }
    catch (e) { setErr(e.message); }
  }
  function preview(theme) { previewAppTheme(theme, activeTheme, 3000); }
  async function applyFontPick(code) {
    try { await api("/api/profile/font", { method: "POST", body: { font: code } }); applyFont(code); setActiveFont(code); onBought && onBought(); }
    catch (e) { setErr(e.message); }
  }
  function previewFontPick(code) { previewFont(code, activeFont, 3000); }
  // f-batch — „pri otvorení" ukážka ostáva padajúca (EffectBurst, nezmenené); „po splnení" ukážka
  // teraz vychádza z bodu ťuku (rovnaká mechanika ako v appke — CompletionBurst).
  function previewEffect(trigger, code, evt) {
    if (REDUCE_MOTION) return;
    if (trigger === "done") {
      const el = evt && evt.currentTarget;
      const r = el && el.getBoundingClientRect ? el.getBoundingClientRect() : null;
      if (!r) return;
      setDoneBurst({ key: code + ":" + Math.round(performance.now()), effect: code, rect: { x: r.left, y: r.top, w: r.width, h: r.height } });
      return;
    }
    setShopBurst(code); setTimeout(() => setShopBurst(null), 2600);
  }
  async function setEffect(trigger, e) {
    const body = trigger === "open"
      ? { open_effect: e, done_effect: s.done_effect || "confetti" }
      : { open_effect: s.open_effect || "", done_effect: e };
    try { await api("/api/profile/effects", { method: "POST", body }); onBought && onBought(); }
    catch (err) { setErr(err.message); }
  }

  const groups = { consumable: [], color: [], cosmetic: [], headwear: [], font: [], anim: [], milestone: [] };
  if (data) for (const it of data.items) { if (groups[it.kind]) groups[it.kind].push(it); }
  // v30 — položky vrstvy „navrch" (slot z katalógu, dnes len svätožiara) dostanú vlastnú sekciu,
  // nech je jasné, že sa nosia SPOLU s ozdobou na hlavu a nesúperia s ňou o jedno miesto.
  const overItems = groups.cosmetic.filter((it) => it.slot === "over");
  const plainCosmetics = groups.cosmetic.filter((it) => it.slot !== "over");
  const animOwned = {};  // code → owned (z katalógu)
  if (data) for (const it of groups.anim) animOwned[it.code] = it.owned;
  // fallback = cenový stupeň 6 (ANIM_COST v app.py, v31 §6.3d) — použije sa len kým sa katalóg načíta
  const animCost = (groups.anim[0] && groups.anim[0].cost) || 1600;
  // zladené s FONT_FAMILY v lib/theme.ts (fonty menia nadpisy + mono-labely)
  const FONT_FAM = { nunito: "'Nunito', sans-serif", poppins: "'Poppins', sans-serif", schibsted: "'Schibsted Grotesk', sans-serif", fraunces: "'Fraunces', Georgia, serif", bricolage: "'Bricolage Grotesque', sans-serif" };

  // v2 — every shop row is a ds ListRow (Lead chip · title/desc · trailing) inside a List
  // card. Same padding/divider/leading box; only the trailing slot + lead colour change.
  const hlStyle = (code) => (hl === code ? { outline: "2px solid var(--brand-primary)", outlineOffset: -2 } : undefined);
  // JEDNO miesto pre tlačidlo „Skús" v celom Obchode (farby, písma, animácie) — predtým
  // sa ten istý inline literál opakoval pri každom type položky (§6a bod 5).
  // Ozdoby na hlavu tlačidlo NEMAJÚ: skúšajú sa ťuknutím na celý riadok (viď `plainRow`).
  const tryBtn = { ...st.ghostBtn, flex: "none", padding: "7px 10px" };
  // `stopPropagation`: pri klikacích riadkoch (ozdoby) nesmie ťuk na tlačidlo v pravom stĺpci
  // spustiť zároveň aj ukážku (vzor ds ListRow — ChallengesTab to robí rovnako).
  const priceBtn = (code, cost) => (
    <button onClick={(e) => { e.stopPropagation(); buy(code); }} disabled={busy === code || data.balance < cost}
      style={{ ...st.doneBtn, flex: "none", ...(data.balance < cost ? { opacity: 0.45 } : {}) }}>{busy === code ? "…" : cost + " b."}</button>
  );
  const colorRow = (it) => {
    const col = SWATCH[it.theme] || "var(--accent)";
    const isActive = it.owned && activeTheme === it.theme;
    return (
      <ListRow key={it.code} data-shopcode={it.code} style={hlStyle(it.code)}
        lead={<Lead style={{ background: col, color: "#fff", border: "1px solid var(--border)" }}>{!it.owned && <Icon name="lock" size={14} color="#fff" />}</Lead>}
        title={it.name} meta={it.owned ? "Odomknuté" : "Skús — ukáže farbu na 3 s."}
        trailing={it.owned
          ? (isActive ? <span style={st.shopOwned}><Chk />aktívna</span> : <button onClick={() => applyColor(it.theme)} style={{ ...st.doneBtn, flex: "none" }}>Použiť</button>)
          : <span style={{ display: "inline-flex", gap: 6 }}><button onClick={() => preview(it.theme)} style={tryBtn}>Skús</button>{priceBtn(it.code, it.cost)}</span>} />
    );
  };
  const plainRow = (it) => {
    const ic = SHOP_ITEM_ICON[it.code];
    const equipCode = it.code.startsWith("frame_") || it.code.startsWith("flair_");
    // v30 — dve nezávislé vrstvy ozdôb: slot "head" → `equipped_flair`, "over" → `equipped_over`.
    // Slot berieme z katalógu (backend), nehádame ho z kódu.
    const slotCol = it.code.startsWith("frame_") ? "equipped_frame"
      : it.slot === "over" ? "equipped_over" : "equipped_flair";
    const on = !!(s.user && s.user[slotCol] === it.code);
    // v30 — ozdoby na hlavu majú namiesto ikony rovno svoju kresbu na naznačenej hlave,
    // nech je pred kúpou vidno, čo si človek nasadzuje (rovnaký ListRow, len iný Lead).
    const head = HEAD_FLAIRS[it.code];
    // v32 — SKÚŠOBŇA sa spúšťa ŤUKNUTÍM NA CELÝ RIADOK (žiadne tlačidlo „Skús" navyše: to zúžilo
    // textový stĺpec na 100 px a dlhé názvy sa lámali aj uprostred slova). Riadok tak vyzerá presne
    // ako pred v32 a objaviteľnosť nesie text v `meta` — rovnako to robí `colorRow`.
    // Nemá zmysel pri ozdobe, ktorú už človek reálne nosí (`on`) — tú vidí naživo všade.
    const canTry = !!head && !on;
    const isTrying = !!tryOn && tryOn.code === it.code;
    // druhý ťuk na tú istú ozdobu ukážku zruší (rovnaké gesto tam aj späť)
    const rowTry = canTry
      ? () => setTryOn(isTrying ? null : { code: it.code, slot: it.slot === "over" ? "over" : "head", name: it.name })
      : undefined;
    // V `meta` je LEN stav („práve toto skúšaš"), nie pokyn. Pripomienka „Ťukni." pri každom
    // riadku vyzerala na 360 px ako šum (šesťkrát na obrazovke) a dvom ozdobám pridala riadok
    // navyše — gesto vysvetľuje úvodná veta sekcie a riadok sám vyzerá ako ostatné klikacie
    // riadky v appke. Keby sa v teste ukázalo, že to ľudia neobjavia, vráti sa to sem.
    const hint = canTry && isTrying ? "Práve skúšaš." : "";
    return (
      <ListRow key={it.code} data-shopcode={it.code} style={hlStyle(it.code)} onClick={rowTry}
        lead={head
          ? <Lead tone="outline"><HeadFlair code={it.code} size={26} preview /></Lead>
          : it.code.startsWith("frame_")
          ? <Lead tone="outline"><FramePreview frame={it.code} size={22} /></Lead>
          : <Lead style={{ background: (ic && ic.color) || "var(--brand-primary)", color: "#fff", border: "none" }}>{ic ? <BuzerIcon name={ic.name} size={20} color="#fff" /> : it.icon}</Lead>}
        title={it.name} meta={[it.desc, hint].filter(Boolean).join(" ")}
        trailing={it.owned
          ? (equipCode
              ? <button type="button" role="switch" aria-checked={!!on} aria-label={it.name} onClick={(e) => { e.stopPropagation(); equip(it.code, !on); }} style={{ ...st.switch, flex: "none", ...(on ? st.switchOn : {}) }}><span style={{ ...st.switchKnob, ...(on ? st.switchKnobOn : {}) }} /></button>
              : <span style={st.shopOwned}><Chk />máš</span>)
          : priceBtn(it.code, it.cost)} />
    );
  };
  const fontRow = (it) => {
    // v34 — predvolené písmo (font: "") = skutočný default appky = Bricolage Grotesque (--ff-display)
    const fam = it.font ? (FONT_FAM[it.font] || "inherit") : "var(--ff-display)";
    const isActive = it.owned && activeFont === it.font;
    return (
      <ListRow key={it.code} data-shopcode={it.code} style={hlStyle(it.code)}
        lead={<Lead style={{ fontFamily: fam, fontWeight: 800, fontSize: 18 }}>Ag</Lead>}
        title={<span style={{ fontFamily: fam }}>{it.name}</span>} meta={<span style={{ fontFamily: fam }}>Tvoje písmo v appke.</span>}
        trailing={it.owned
          ? (isActive ? <span style={st.shopOwned}><Chk />aktívne</span> : <button onClick={() => applyFontPick(it.font)} style={{ ...st.doneBtn, flex: "none" }}>Použiť</button>)
          : <span style={{ display: "inline-flex", gap: 6 }}><button onClick={() => previewFontPick(it.font)} style={tryBtn}>Skús</button>{priceBtn(it.code, it.cost)}</span>} />
    );
  };
  // v2.1 — milestoneRow: odmena za tier levelov. Zamknutá = lock + požiadavka; odomknutá =
  // rovnaký equip prepínač ako kúpené doplnky (owned príde z backendu podľa tieru).
  const milestoneRow = (it) => {
    const ic = SHOP_ITEM_ICON[it.code];
    const on = s.user && s.user.equipped_flair === it.code;
    return (
      <ListRow key={it.code} data-shopcode={it.code} style={hlStyle(it.code)}
        lead={<Lead style={{ background: it.owned ? ((ic && ic.color) || "var(--brand-primary)") : "var(--muted)", color: "#fff", border: "none" }}>
          {it.owned && ic ? <BuzerIcon name={ic.name} size={20} color="#fff" /> : <Icon name="lock" size={16} color="#fff" />}</Lead>}
        title={it.name} meta={it.owned ? it.desc : `Odomkne sa na tieri ${it.tier_name}.`}
        trailing={it.owned
          ? <button type="button" role="switch" aria-checked={!!on} aria-label={it.name} onClick={() => equip(it.code, !on)} style={{ ...st.switch, flex: "none", ...(on ? st.switchOn : {}) }}><span style={{ ...st.switchKnob, ...(on ? st.switchKnobOn : {}) }} /></button>
          : <span style={{ ...st.taskDesc, display: "inline-flex", alignItems: "center", gap: 5, flex: "none" }}><Icon name="lock" size={13} color="var(--muted)" />zamknuté</span>} />
    );
  };
  // animRow — efekt pre daný spúšťač (open/done); konfety pri splnení sú zadarmo (default)
  const animRow = (trigger, eff) => {
    const code = `anim_${trigger}_${eff.code}`;
    const owned = (trigger === "done" && eff.code === "confetti") || animOwned[code];
    const active = (trigger === "open" ? (s.open_effect || "") : (s.done_effect || "confetti")) === eff.code;
    // „confetti" v sekcii „pri splnení" = pôvodné pestré guličky (default) — vlastný názov+ikona,
    // nech je jasné, že je to predvolený rozpŕskavajúci efekt loptičiek (nie padajúce konfety).
    const isDoneDefault = trigger === "done" && eff.code === "confetti";
    const label = isDoneDefault ? "Guličky" : eff.label;
    const glyph = isDoneDefault ? "party" : (eff.icon || "sparkles");
    const metaTxt = owned ? (active ? "Práve aktívna" : "Klikni Použiť") : "Skús a potom odomkni";
    return (
      <ListRow key={code}
        lead={<Lead style={{ color: eff.color || "var(--accent)" }}><BuzerIcon name={glyph} size={20} color={eff.color || "var(--accent)"} /></Lead>}
        title={label} meta={isDoneDefault ? `${metaTxt} · predvolené` : metaTxt}
        trailing={<span style={{ display: "inline-flex", gap: 6 }}>
          <button onClick={(evt) => previewEffect(trigger, eff.code, evt)} style={tryBtn}>Skús</button>
          {owned
            ? (active ? <span style={st.shopOwned}><Chk />aktívna</span> : <button onClick={() => setEffect(trigger, eff.code)} style={{ ...st.doneBtn, flex: "none" }}>Použiť</button>)
            : priceBtn(code, animCost)}
        </span>} />
    );
  };
  const SectionLabel = ({ children }) => <SectionHeader label={children} style={{ margin: "16px 2px 6px" }} />;

  // SKÚŠOBŇA — používateľ so ZAMENENOU jednou vrstvou. Kľúčové: meníme LEN slot skúšanej ozdoby,
  // druhá vrstva (svätožiara nad čiapkou, resp. čiapka pod svätožiarou), kúpený rámik, levelový
  // prsteň aj admin odlesk ostávajú presne také, aké ich človek naozaj má — kreslí ich ten istý
  // `Avatar` ako všade inde, takže sa ukážka nemôže rozísť so skutočnosťou (§6a bod 5).
  const meUser = s.user || {};
  const tryUser = tryOn
    ? { ...meUser, [tryOn.slot === "over" ? "equipped_over" : "equipped_flair"]: tryOn.code }
    : meUser;

  // v32.23 — bol vlastný ručne stavaný bottom-sheet (st.sheet*); teraz zdieľaný Modal (center
  // varianta) ako ostatné detailové modaly (napr. NotifPrefsModal nižšie) — centrovaná karta,
  // žiadna horná „handle" čiarka.
  return (
    <>
      {shopBurst && <EffectBurst effect={shopBurst} />}
      {doneBurst && <CompletionBurst key={doneBurst.key} effect={doneBurst.effect} rect={doneBurst.rect} onDone={() => setDoneBurst(null)} />}
      {/* Skúšobňa ozdôb — v pätičke modalu (mimo scrollujúceho tela), takže je vidieť pri
          ktoromkoľvek riadku Obchodu; keď sa nič neskúša, pätička vôbec nie je (žiadne miesto navyše). */}
      <Modal center title="Obchod za body" icon="shop" onClose={onClose} maxWidth={480}
        footer={tryOn ? (
          <div className="bz-tryon">
            <div className="bz-tryon__ava"><Avatar user={tryUser} size={72} /></div>
            <div className="bz-tryon__body" aria-live="polite">
              <div className="bz-tryon__title">{tryOn.name}</div>
              <div className="bz-tryon__meta">Len ukážka, nič sa neukladá.</div>
              <Button variant="secondary" size="sm" onClick={() => setTryOn(null)}>Zrušiť ukážku</Button>
            </div>
          </div>
        ) : undefined}>
        {!data ? <div style={{ display: "flex", justifyContent: "center", padding: 20 }}><div className="spin" /></div> : (
          <>
            <div style={st.shopBal}><BuzerIcon name="shop" size={15} color="var(--text)" style={{ verticalAlign: "-2px", marginRight: 5 }} /><strong>{data.balance}</strong> bodov na míňanie · <BuzerIcon name="snow" size={14} color="var(--text2)" style={{ verticalAlign: "-2px" }} /> <strong>{data.freezes}</strong> záchranných kruhov</div>
            {err && <div style={st.err}>{err}</div>}
            {groups.consumable.length > 0 && <List>{groups.consumable.map(plainRow)}</List>}
            <SectionLabel>Farby témy</SectionLabel>
            <div style={{ ...st.sheetDesc, margin: "0 2px 8px" }}>Niekoľko farieb máš zadarmo. Ostatné si odomkni — najprv si ich vyskúšaj na pár sekúnd.</div>
            <List>{groups.color.map(colorRow)}</List>
            {groups.font.length > 0 && <SectionLabel>Písma</SectionLabel>}
            {/* v33h — „Predvolené písmo" (zadarmo, vždy odomknuté) ako prvé, nech sa dá vrátiť späť
                na pôvodné písmo appky po kúpe a nasadení iného. */}
            {groups.font.length > 0 && <List>{[{ code: "font_default", font: "", name: "Predvolené písmo", owned: true }, ...groups.font].map(fontRow)}</List>}
            {groups.anim.length > 0 && (<>
              <SectionLabel>Animácie · pri otvorení appky</SectionLabel>
              <List>
                <ListRow lead={<Lead><Icon name="close" size={18} color="var(--muted)" /></Lead>} title="Žiadna" meta="Bez animácie pri otvorení."
                  trailing={(!s.open_effect) ? <span style={st.shopOwned}><Chk />aktívna</span> : <button onClick={() => setEffect("open", "")} style={{ ...st.doneBtn, flex: "none" }}>Použiť</button>} />
                {EFFECTS.map((eff) => animRow("open", eff))}
              </List>
              <SectionLabel>Animácie · pri splnení návyku</SectionLabel>
              <List>{EFFECTS.map((eff) => animRow("done", eff))}</List>
            </>)}
            {plainCosmetics.length > 0 && <SectionLabel>Doplnky</SectionLabel>}
            {plainCosmetics.length > 0 && <List>{plainCosmetics.map(plainRow)}</List>}
            {groups.headwear.length > 0 && (<>
              <SectionLabel>Ozdoby na hlavu</SectionLabel>
              <div style={{ ...st.sheetDesc, margin: "0 2px 8px" }}>Nosia sa na tvojej profilovke — vidia ich aj priatelia. Naraz môžeš mať nasadenú jednu ozdobu. Ťukni na ozdobu a uvidíš ju dole na svojej fotke.</div>
              <List>{groups.headwear.map(plainRow)}</List>
            </>)}
            {/* v30 — vlastná vrstva „navrch": svätožiara sa nosí NAD ozdobou na hlavu,
                takže nezaberá miesto čiapke ani rožkom (backendový `slot: "over"`). */}
            {overItems.length > 0 && (<>
              <SectionLabel>Navrch ozdoby</SectionLabel>
              <div style={{ ...st.sheetDesc, margin: "0 2px 8px" }}>Táto vrstva sa nosí navrch — môžeš ju mať zapnutú SPOLU s ozdobou na hlavu a miesto jej nezaberá.</div>
              <List>{overItems.map(plainRow)}</List>
            </>)}
            {groups.milestone.length > 0 && (<>
              <SectionLabel>Odmeny za levely</SectionLabel>
              <div style={{ ...st.sheetDesc, margin: "0 2px 8px" }}>Tieto sa nedajú kúpiť — odomkneš ich dosiahnutím tieru levelov.</div>
              <List>{groups.milestone.map(milestoneRow)}</List>
            </>)}
          </>
        )}
      </Modal>
    </>
  );
}
// PR6/PR7 — per-type notification preferences (in-app/push + e-mail) + master + push enable
export function NotifPrefsModal({ onClose }) {
  const [data, setData] = useState(null);
  const [err, setErr] = useState("");
  const installed = isStandalone();
  useEffect(() => { api("/api/profile/notif-prefs").then(setData).catch((e) => setErr(e.message)); }, []);
  function persist(next) {
    setData(next);
    const body = { master: next.master,
      app: Object.fromEntries(next.app.map((t) => [t.key, t.on])),
      push: Object.fromEntries(next.app.filter((t) => t.pushable).map((t) => [t.key, t.push])),
      email: Object.fromEntries(next.email.map((t) => [t.key, t.on])) };
    api("/api/profile/notif-prefs", { method: "POST", body }).catch(() => {});
  }
  const toggleMaster = () => persist({ ...data, master: !data.master });
  const toggleApp = (k) => persist({ ...data, app: data.app.map((t) => (t.key === k ? { ...t, on: !t.on } : t)) });
  const togglePush = (k) => persist({ ...data, app: data.app.map((t) => (t.key === k ? { ...t, push: !t.push } : t)) });
  const toggleEmail = (k) => persist({ ...data, email: data.email.map((t) => (t.key === k ? { ...t, on: !t.on } : t)) });
  async function enable() {
    if (!installed) { alert("Najprv pridaj appku na plochu (Zdieľať → Pridať na plochu), potom zapni upozornenia."); return; }
    await enablePush();
  }
  // v26.3 §7 — trvalá diagnostika: pošli skúšobný push a ukáž výsledok (sent/subs + chyby)
  const [testMsg, setTestMsg] = useState("");
  const [testBusy, setTestBusy] = useState(false);
  async function runTest() {
    setTestBusy(true); setTestMsg("Odosielam…");
    try {
      const r = await testPush();
      if (r.sent > 0) setTestMsg(`Odoslané na ${r.sent} z ${r.subs} zariadení. Ak nič nepríde, skontroluj povolenia notifikácií v prehliadači aj v systéme.`);
      else if (!r.subs) setTestMsg("Žiadne zariadenie nie je prihlásené na notifikácie — zapni ich vyššie „Zapnúť na tomto zariadení“.");
      else setTestMsg(`Nedoručené na ${r.subs} zariadení: ${(r.errors || []).join("; ") || "neznáma chyba"}.`);
    } catch (e) { setTestMsg("Skúšobnú notifikáciu sa nepodarilo odoslať."); }
    setTestBusy(false);
  }
  function Switch({ on, onClick, dim }) {
    return (
      <button type="button" role="switch" aria-checked={on} onClick={onClick}
        style={{ ...st.switch, ...(on ? st.switchOn : {}), flex: "none", opacity: dim ? 0.4 : 1, pointerEvents: dim ? "none" : "auto" }}>
        <span style={{ ...st.switchKnob, ...(on ? st.switchKnobOn : {}) }} />
      </button>
    );
  }
  function Row({ label, desc, on, onToggle, dim }) {
    return (
      <div style={{ ...st.profRow, cursor: "default", opacity: dim ? 0.5 : 1 }}>
        <span style={{ flex: 1, textAlign: "left", minWidth: 0 }}>
          <span style={st.sheetLabel}>{label}</span>
          {desc ? <span style={st.sheetDesc}>{desc}</span> : null}
        </span>
        <Switch on={on} onClick={onToggle} dim={dim} />
      </div>
    );
  }
  // v33+ — riadok in-app upozornenia s DVOMA prepínačmi: „Appka" (zvonček) a „Push" (telefón).
  // Push je podriadený zvončeku (keď je zvonček vypnutý, push sa zošedí), a typy, ktoré push
  // vôbec neposielajú (nový level, denné zhrnutie, nálada) ukazujú „–" namiesto prepínača.
  function AppRow({ t }) {
    const bellOn = data.master && t.on;
    return (
      <div style={{ ...st.profRow, cursor: "default", opacity: data.master ? 1 : 0.5 }}>
        <span style={{ flex: 1, textAlign: "left", minWidth: 0 }}>
          <span style={st.sheetLabel}>{t.label}</span>
          {t.desc ? <span style={st.sheetDesc}>{t.desc}</span> : null}
        </span>
        <span style={st.notifSwitchCell}><Switch on={bellOn} dim={!data.master} onClick={() => toggleApp(t.key)} /></span>
        <span style={st.notifSwitchCell}>
          {t.pushable
            ? <Switch on={bellOn && t.push} dim={!bellOn} onClick={() => togglePush(t.key)} />
            : <span style={st.statLabel}>–</span>}
        </span>
      </div>
    );
  }
  return (
    <Modal center title="Upozornenia" icon="bell" onClose={onClose} maxWidth={480}>
      {err && <div style={st.err}>{err}</div>}
      {!data ? <div style={{ display: "flex", justifyContent: "center", padding: 24 }}><div className="spin" /></div> : (
        <>
          {/* PR7 — push needs the installed app first */}
          <div style={{ ...st.infoNote, marginBottom: 12 }}>
            {installed
              ? <>Push notifikácie fungujú na tomto zariadení. <button type="button" onClick={enable} style={{ ...st.linkBtn, padding: 0 }}>Zapnúť na tomto zariadení ›</button></>
              : "📲 Push notifikácie fungujú až po pridaní appky na plochu. Otvor ju z plochy a vráť sa sem."}
          </div>
          {/* v26.3 §7 — skúšobná notifikácia (na každom zariadení zistí, či server vie doručiť) */}
          {installed && (
            <div style={{ marginBottom: 12 }}>
              <button type="button" onClick={runTest} disabled={testBusy}
                style={{ ...st.reorderToggle, opacity: testBusy ? 0.6 : 1 }}>Odoslať skúšobnú notifikáciu</button>
              {testMsg && <div style={{ ...st.sheetDesc, marginTop: 6 }}>{testMsg}</div>}
            </div>
          )}
          {/* master */}
          <Row label="Všetky upozornenia" desc="Hlavný vypínač — keď je vypnutý, neposielame nič." on={data.master} onToggle={toggleMaster} />
          <div style={{ ...st.progressHead, marginTop: 10 }}>
            <span className="bz-subhead">V aplikácii a push</span>
            <span style={{ display: "flex", gap: 12 }}>
              <span style={{ ...st.notifSwitchCell, ...st.statLabel }}>Appka</span>
              <span style={{ ...st.notifSwitchCell, ...st.statLabel }}>Push</span>
            </span>
          </div>
          {data.app.map((t) => <AppRow key={t.key} t={t} />)}
          <div style={{ ...st.progressHead, marginTop: 14 }}><span className="bz-subhead">E-mailom</span></div>
          {data.email.map((t) => (
            <Row key={t.key} label={t.label} desc={t.desc} on={data.master && t.on} dim={!data.master}
              onToggle={() => toggleEmail(t.key)} />
          ))}
        </>
      )}
    </Modal>
  );
}
